import Card, { CardHeader, CardBody } from '../../components/ui/Card';
import { useState, useEffect, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vermieterApi, type VermieterCreateData } from '../../api/vermieter';
import VerfahrenStateBadge from '../../components/proceedings/VerfahrenStateBadge';
import AnfragenStateBadge from '../../components/requests/AnfragenStateBadge';
import InseratBlock from '../../components/listings/InseratBlock';
import ConfirmDeleteModal from '../../components/ui/ConfirmDeleteModal';
import MapLinkButton from '../../components/maps/MapLinkButton';
import { restoreOne } from '../../api/softDelete';
import { useCan } from '../../hooks/useCan';
import { useIsModuleEnabled } from '../../hooks/useConfigVisibility';
import { useReturnTo } from '../../hooks/usePageNavigation';

const ANREDE_DATALIST_ID = 'vermieter-anrede-options';
const ANREDE_OPTIONS = ['Herr', 'Frau', 'Firma'];

interface StammdatenForm {
  anrede: string;
  name: string;
  typ: string;
  email: string;
  telefon: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land: string;
  geburtsdatum: string;
  notes: string;
}

const fieldClass =
  'w-full bg-transparent border border-gray-300 rounded px-2 py-1 text-sm text-gray-900 ' +
  'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors';

function FormRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 sm:mt-0 sm:col-span-2">{children}</dd>
    </div>
  );
}

function buildForm(v: {
  anrede: string | null;
  name: string;
  typ: string | null;
  email: string | null;
  telefon: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
  geburtsdatum: string | null;
  notes: string | null;
}): StammdatenForm {
  return {
    anrede: v.anrede ?? '',
    name: v.name ?? '',
    typ: v.typ ?? '',
    email: v.email ?? '',
    telefon: v.telefon ?? '',
    strasse: v.strasse ?? '',
    hausnummer: v.hausnummer ?? '',
    plz: v.plz ?? '',
    ort: v.ort ?? '',
    land: v.land ?? '',
    geburtsdatum: v.geburtsdatum ?? '',
    notes: v.notes ?? '',
  };
}

export default function VermieterDetail() {
  const { vermieterId } = useParams<{ vermieterId: string }>();
  const navigate = useNavigate();
  const returnTo = useReturnTo('/vermieter');
  const queryClient = useQueryClient();
  const showInserate = useIsModuleEnabled('inserate');
  const showAnfragen = useIsModuleEnabled('anfragen');
  const showVerfahren = useIsModuleEnabled('verfahren');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: vermieter, isLoading, error } = useQuery({
    queryKey: ['vermieter', vermieterId],
    queryFn: () => vermieterApi.getVermieterDetail(Number(vermieterId)),
    enabled: !!vermieterId,
  });

  const { data: inserate } = useQuery({
    queryKey: ['vermieter', vermieterId, 'inserate'],
    queryFn: () => vermieterApi.getInserate(Number(vermieterId)),
    enabled: !!vermieterId,
  });

  const { data: anfragenList } = useQuery({
    queryKey: ['vermieter', vermieterId, 'anfragen'],
    queryFn: () => vermieterApi.getAnfragen(Number(vermieterId)),
    enabled: !!vermieterId,
  });

  const { data: verfahrenList } = useQuery({
    queryKey: ['vermieter', vermieterId, 'verfahren'],
    queryFn: () => vermieterApi.getVerfahren(Number(vermieterId)),
    enabled: !!vermieterId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<VermieterCreateData>) =>
      vermieterApi.updateVermieter(Number(vermieterId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vermieter', vermieterId] });
    },
  });

  const [form, setForm] = useState<StammdatenForm | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (vermieter) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize the editable form when the requested landlord loads.
      setForm(buildForm(vermieter));
    }
  }, [vermieter]);

  const initialForm = vermieter ? buildForm(vermieter) : null;
  const isDirty =
    !!form && !!initialForm && JSON.stringify(form) !== JSON.stringify(initialForm);

  const handleSaveStammdaten = () => {
    if (!form) return;
    updateMutation.mutate(
      {
        anrede: form.anrede || undefined,
        name: form.name,
        typ: form.typ || undefined,
        email: form.email || undefined,
        telefon: form.telefon || undefined,
        strasse: form.strasse || undefined,
        hausnummer: form.hausnummer || undefined,
        plz: form.plz || undefined,
        ort: form.ort || undefined,
        land: form.land || undefined,
        geburtsdatum: form.geburtsdatum || null,
        notes: form.notes || undefined,
      },
      {
        onSuccess: () => {
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 2000);
        },
      },
    );
  };

  const canRestore = useCan('restore_own');
  const restoreMutation = useMutation({
    mutationFn: () => restoreOne('vermieter', Number(vermieterId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vermieter'] });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !vermieter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Vermieter konnte nicht geladen werden.</p>
          <button onClick={() => navigate(returnTo)} className="btn btn-text mt-4">
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate(returnTo)} className="btn btn-text mb-2 text-sm">
            &larr; Zurück zur Übersicht
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{vermieter.name}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {vermieter.typ && (
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                    vermieter.typ === 'gewerblich' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {vermieter.typ}
                  </span>
                )}
                <span className="text-sm text-gray-500">
                  {vermieter.inserate_count} Inserat{vermieter.inserate_count !== 1 ? 'e' : ''}
                </span>
                <MapLinkButton to="/karte" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {vermieter.deleted_at ? (
                <>
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-200 text-slate-700">
                    Ausgeblendet
                  </span>
                  {canRestore && (
                    <button
                      onClick={() => restoreMutation.mutate()}
                      disabled={restoreMutation.isPending}
                      className="btn btn-secondary bg-success-50 text-success-700 border-success-200 enabled:hover:bg-success-100 focus-visible:ring-success-500 border"
                    >
                      Wiederherstellen
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn btn-secondary bg-danger-50 text-danger-700 border-danger-200 enabled:hover:bg-danger-100 focus-visible:ring-danger-500 border"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        entity="vermieter"
        ids={[Number(vermieterId)]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['vermieter'] });
          navigate(returnTo);
        }}
      />

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Editable fields */}
        <Card>
          <CardHeader title="Stammdaten" />
          <CardBody padding="horizontal">
            <datalist id={ANREDE_DATALIST_ID}>
              {ANREDE_OPTIONS.map((a) => (
                <option key={a} value={a} />
              ))}
            </datalist>
            <dl className="divide-y divide-gray-200">
              <FormRow label="Anrede">
                <input
                  value={form?.anrede ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, anrede: e.target.value })}
                  list={ANREDE_DATALIST_ID}
                  placeholder="Herr / Frau / Firma oder freier Text"
                  className={fieldClass}
                />
              </FormRow>
              <FormRow label="Name">
                <input
                  value={form?.name ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, name: e.target.value })}
                  placeholder="Name eingeben"
                  className={fieldClass}
                />
              </FormRow>
              <FormRow label="Typ">
                <select
                  value={form?.typ ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, typ: e.target.value })}
                  className={fieldClass}
                >
                  <option value="">-- kein Typ --</option>
                  <option value="privatperson">Privatperson</option>
                  <option value="gewerblich">Gewerblich</option>
                </select>
              </FormRow>
              <FormRow label="E-Mail">
                <input
                  type="email"
                  value={form?.email ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, email: e.target.value })}
                  placeholder="Nicht angegeben"
                  className={fieldClass}
                />
              </FormRow>
              <FormRow label="Telefon">
                <input
                  value={form?.telefon ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, telefon: e.target.value })}
                  placeholder="Nicht angegeben"
                  className={fieldClass}
                />
              </FormRow>
              <FormRow label="Straße">
                <div className="flex gap-2">
                  <input
                    value={form?.strasse ?? ''}
                    onChange={(e) => setForm((f) => f && { ...f, strasse: e.target.value })}
                    placeholder="Straße"
                    className={`${fieldClass} flex-1`}
                  />
                  <input
                    value={form?.hausnummer ?? ''}
                    onChange={(e) => setForm((f) => f && { ...f, hausnummer: e.target.value })}
                    placeholder="Nr."
                    className={`${fieldClass} w-24`}
                  />
                </div>
              </FormRow>
              <FormRow label="PLZ / Ort">
                <div className="flex gap-2">
                  <input
                    value={form?.plz ?? ''}
                    onChange={(e) => setForm((f) => f && { ...f, plz: e.target.value })}
                    placeholder="PLZ"
                    className={`${fieldClass} w-24`}
                  />
                  <input
                    value={form?.ort ?? ''}
                    onChange={(e) => setForm((f) => f && { ...f, ort: e.target.value })}
                    placeholder="Ort"
                    className={`${fieldClass} flex-1`}
                  />
                </div>
              </FormRow>
              <FormRow label="Land">
                <input
                  value={form?.land ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, land: e.target.value })}
                  placeholder="Deutschland"
                  className={fieldClass}
                />
              </FormRow>
              <FormRow label="Geburtsdatum">
                <input
                  type="date"
                  value={form?.geburtsdatum ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, geburtsdatum: e.target.value })}
                  className={fieldClass}
                />
              </FormRow>
              <FormRow label="Notizen">
                <textarea
                  value={form?.notes ?? ''}
                  onChange={(e) => setForm((f) => f && { ...f, notes: e.target.value })}
                  placeholder="Keine Notizen"
                  rows={2}
                  className={`${fieldClass} resize-none`}
                />
              </FormRow>
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Erstellt</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {new Date(vermieter.created_at).toLocaleString('de-DE')}
                </dd>
              </div>
              {vermieter.aktiv_seit && (
                <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt className="text-sm font-medium text-gray-500">Aktiv seit</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {new Date(vermieter.aktiv_seit).toLocaleDateString('de-DE')}
                  </dd>
                </div>
              )}
            </dl>
          </CardBody>
          <CardBody className="border-t flex items-center justify-end gap-3">
            {savedFlash && (
              <span className="text-sm text-green-600">Gespeichert</span>
            )}
            {isDirty && !savedFlash && (
              <span className="text-sm text-gray-500">Ungespeicherte Änderungen</span>
            )}
            <button
              onClick={() => setForm(initialForm)}
              disabled={!isDirty || updateMutation.isPending}
              className="btn btn-outline bg-white"
            >
              Verwerfen
            </button>
            <button
              onClick={handleSaveStammdaten}
              disabled={!isDirty || updateMutation.isPending}
              className="btn btn-primary"
            >
              {updateMutation.isPending ? 'Speichert…' : 'Speichern'}
            </button>
          </CardBody>
        </Card>

        {showInserate && (
        <Card>
          <CardHeader title={<>
              Inserate
              <span className="ml-2 text-sm font-normal text-gray-500">({vermieter.inserate_count})</span>
            </>} />
          <CardBody className="space-y-3">
            {inserate && inserate.length > 0 ? (
              inserate.map((inserat) => (
                <InseratBlock key={inserat.id} inserat={inserat} />
              ))
            ) : (
              <p className="text-sm text-gray-500">Keine Inserate verknüpft.</p>
            )}
          </CardBody>
        </Card>
        )}

        {showAnfragen && (
        <Card className="overflow-hidden">
          <CardHeader title={<>
              Anfragen
              <span className="ml-2 text-sm font-normal text-gray-500">({anfragenList?.length ?? 0})</span>
            </>} />
          {anfragenList && anfragenList.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notizen</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {anfragenList.map((a) => (
                  <tr
                    key={a.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/anfragen/${a.id}`)}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-blue-600">
                      {a.adresse_text || `#${a.id}`}
                      {a.zipcode && <span className="text-gray-500 font-normal ml-1">{a.zipcode}</span>}
                      {a.city && <span className="text-gray-500 font-normal ml-1">{a.city}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <AnfragenStateBadge status={a.status} small />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{a.notes || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(a.created_at).toLocaleDateString('de-DE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              Keine Anfragen verknüpft.
            </div>
          )}
        </Card>
        )}

        {showVerfahren && (
        <Card className="overflow-hidden">
          <CardHeader title={<>
              Verfahren
              <span className="ml-2 text-sm font-normal text-gray-500">({verfahrenList?.length ?? 0})</span>
            </>} />
          {verfahrenList && verfahrenList.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inserate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {verfahrenList.map((v) => (
                  <tr
                    key={v.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/verfahren/${v.id}`)}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-blue-600">
                      {v.aktenzeichen || `Verfahren #${v.id}`}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <VerfahrenStateBadge staat={v.staat} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-xs">
                      {v.adresse_str || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{v.inserate_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(v.created_at).toLocaleDateString('de-DE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              Keine Verfahren verknüpft.
            </div>
          )}
        </Card>
        )}
      </main>
    </div>
  );
}
